import React, { useEffect } from 'react';
import { Container } from '../components/ui/Container/Container';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { PublicFooter } from '../components/Layout/PublicFooter';
import { formatDate } from '@/lib/localeFormatters';

export function PrivacyPolicy() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-muted">
      <PublicHeader />

      {/* Main Content */}
      <main className="py-16">
        <Container size="md">
          <div className="bg-card rounded-lg shadow-sm p-8 md:p-12">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
                <p className="text-lg text-muted-foreground">
                  Last updated: {formatDate(new Date())}
                </p>
              </div>

              <div className="prose prose-lg max-w-none">
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
                  <p className="text-muted-foreground mb-4">
                    At Rocket Routine GmbH ("we," "our," or "us"), we are committed to protecting your privacy and ensuring the security of your personal information.
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our social media asset creation platform.
                  </p>
                  <p className="text-muted-foreground">
                    By using our service, you agree to the collection and use of information in accordance with this policy.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">2. Information We Collect</h2>

                  <h3 className="text-xl font-medium text-foreground mb-3">2.1 Personal Information</h3>
                  <p className="text-muted-foreground mb-4">We may collect the following personal information:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Name and email address</li>
                    <li>Account credentials (encrypted)</li>
                    <li>Profile information you provide</li>
                    <li>Payment information (processed securely by third-party providers)</li>
                    <li>Communication preferences</li>
                  </ul>

                  <h3 className="text-xl font-medium text-foreground mb-3">2.2 Usage Information</h3>
                  <p className="text-muted-foreground mb-4">We automatically collect certain information when you use our service:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Device information (IP address, browser type, operating system)</li>
                    <li>Usage patterns and feature interactions</li>
                    <li>Log files and error reports</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>

                  <h3 className="text-xl font-medium text-foreground mb-3">2.3 Content Information</h3>
                  <p className="text-muted-foreground">
                    Files, documents, and data you upload for company management are stored securely and used solely for providing our service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">3. How We Use Your Information</h2>
                  <p className="text-muted-foreground mb-4">We use your information to:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Provide and maintain our service</li>
                    <li>Process transactions and manage your account</li>
                    <li>Improve our platform and develop new features</li>
                    <li>Send you important updates and communications</li>
                    <li>Provide customer support</li>
                    <li>Ensure platform security and prevent fraud</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">4. Information Sharing and Disclosure</h2>
                  <p className="text-muted-foreground mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li><strong>Service Providers:</strong> Trusted third-party services that help us operate (payment processors, cloud storage)</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                    <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                    <li><strong>With Your Consent:</strong> When you explicitly agree to sharing</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">5. Data Security</h2>
                  <p className="text-muted-foreground mb-4">
                    We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
                    This includes encryption of sensitive data, secure servers, and regular security audits.
                  </p>
                  <p className="text-muted-foreground">
                    While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">6. Cookies and Tracking Technologies</h2>
                  <p className="text-muted-foreground mb-4">
                    We use cookies and similar technologies to enhance your experience, analyze usage patterns, and provide personalized content.
                    You can control cookie settings through your browser preferences.
                  </p>
                  <p className="text-muted-foreground">
                    Essential cookies are required for the service to function, while analytics cookies help us improve our platform.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">7. Your Rights and Choices</h2>
                  <p className="text-muted-foreground mb-4">You have the right to:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li><strong>Access:</strong> Request a copy of your personal information</li>
                    <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                    <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                    <li><strong>Portability:</strong> Receive your data in a structured format</li>
                    <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                  </ul>
                  <p className="text-muted-foreground">
                    To exercise these rights, please contact us at privacy@rocket-routine.com
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">8. Data Retention</h2>
                  <p className="text-muted-foreground mb-4">
                    We retain your personal information only as long as necessary to provide our service and fulfill the purposes outlined in this policy,
                    unless a longer retention period is required by law.
                  </p>
                  <p className="text-muted-foreground">
                    When you delete your account, we will delete or anonymize your personal information within 30 days,
                    except where retention is required for legal, regulatory, or legitimate business purposes.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">9. International Data Transfers</h2>
                  <p className="text-muted-foreground">
                    Your information may be transferred to and processed in countries other than your own.
                    We ensure appropriate safeguards are in place to protect your data during such transfers.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">10. Children's Privacy</h2>
                  <p className="text-muted-foreground">
                    Our service is not intended for children under 13. We do not knowingly collect personal information from children under 13.
                    If we become aware that we have collected such information, we will delete it immediately.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">11. Changes to This Policy</h2>
                  <p className="text-muted-foreground mb-4">
                    We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page
                    and updating the "Last updated" date.
                  </p>
                  <p className="text-muted-foreground">
                    Your continued use of our service after any changes indicates your acceptance of the updated policy.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">12. Contact Us</h2>
                  <p className="text-muted-foreground mb-4">
                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                  </p>
                  <div className="bg-muted rounded-lg p-4">
                    <p className="text-muted-foreground mb-2"><strong>Email:</strong> privacy@rocket-routine.com</p>
                    <p className="text-muted-foreground mb-2"><strong>Address:</strong> Rocket Routine GmbH - Händelstr. 10 - 76327 Pfinztal - Germany</p>
                    <p className="text-muted-foreground"><strong>Data Protection Officer:</strong> dpo@rocket-routine.com</p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </Container>
      </main>

      <PublicFooter />
    </div>
  );
}
